import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import type { ProjectDocument, ApiProjectRow, Page } from '../types/project';
import type { CanvasActions } from '../store/useProjectStore';
import { useCanvasActions } from '../store/useProjectStore';
import * as api from '../api/projectsApi';
import { validateProject, type ValidationResult } from '../api/validationApi';

// ── Default document used when creating a new project ────────────────────────

function makeNewDoc(name: string): ProjectDocument {
  return {
    schemaVersion: '0.1.0',
    project: {
      id: `proj_${Date.now()}`,
      name,
      pages: [{ id: 'page_main', name: 'Main Page', order: 0, components: [] }],
      actions: [],
      bindings: [],
      variables: {},
      settings: {},
    },
  };
}

// ── Toast message ─────────────────────────────────────────────────────────────

export type ToastKind = 'success' | 'error' | 'info';

export type Toast = {
  id: number;
  kind: ToastKind;
  message: string;
};

// ── Context value type ────────────────────────────────────────────────────────

export type ProjectContextValue = {
  // Current document (canonical project JSON in memory)
  doc: ProjectDocument;
  setDoc: React.Dispatch<React.SetStateAction<ProjectDocument>>;

  // Backend record metadata (null when no project is loaded from backend)
  projectRowId: number | null;
  projectName: string;
  setProjectName: (name: string) => void;

  // Active page (first page, MVP)
  activePage: Page;
  activePageId: string;
  setActivePage: (id:string)=>void;
  addPage:()=>void;
  duplicatePage:(id:string)=>void;
  updatePage:(id:string,changes:Partial<Pick<Page,'name'|'slug'|'showInNavigation'>>)=>void;
  movePage:(id:string,direction:-1|1)=>void;
  deletePage:(id:string)=>void;
  setDefaultPage:(id:string)=>void;

  // Canvas mutation actions (add/move/remove components)
  canvas: CanvasActions;

  // Canvas component selection
  selectedId: string | null;
  selectComponent: (id: string | null) => void;

  // Persistence state
  isDirty: boolean;
  isLoading: boolean;
  isSaving: boolean;
  validation: ValidationResult | null;
  validationPending: boolean;
  validationUnavailable: string | null;

  // Project list (for the load picker)
  projectList: ApiProjectRow[];

  // Operations
  newProject: (name?: string) => void;
  saveProject: () => Promise<void>;
  loadProject: (id: number) => Promise<boolean>;
  refreshProjectList: () => Promise<void>;

  // Toast notifications
  toasts: Toast[];
  dismissToast: (id: number) => void;
};

// ── Context creation ──────────────────────────────────────────────────────────

const ProjectContext = createContext<ProjectContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function ProjectProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  // ── Document state ────────────────────────────────────────────────────────
  const [doc, setDoc] = useState<ProjectDocument>(() => makeNewDoc('New Project'));
  const [projectRowId, setProjectRowId] = useState<number | null>(null);
  const [projectName, setProjectNameState] = useState('New Project');
  const [isDirty, setIsDirty] = useState(false);
  const [activePageId,setActivePageId]=useState(()=>makeNewDoc('temp').project.pages[0].id);

  // ── Canvas mutations (delegate to useCanvasActions) ───────────────────────
  // Wrap setDoc so we can track dirty state on every canvas mutation
  const setDocAndMarkDirty: React.Dispatch<React.SetStateAction<ProjectDocument>> =
    useCallback(
      (action) => {
        setDoc(action);
        setIsDirty(true);
      },
      [],
    );

  const canvas = useCanvasActions(doc, setDocAndMarkDirty,activePageId);
  useEffect(()=>{if(!doc.project.pages.some(page=>page.id===activePageId))setActivePageId(doc.project.settings.defaultPageId??doc.project.pages[0]?.id??'');},[doc.project.pages,doc.project.settings.defaultPageId,activePageId]);
  const setActivePage=useCallback((id:string)=>{setActivePageId(id);setSelectedId(null);},[]);
  const slugify=(value:string)=>value.toLowerCase().trim().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')||'page';
  const uniqueSlug=useCallback((base:string,pages:Page[])=>{const root=slugify(base);let slug=root,index=2;const used=new Set(pages.map(page=>page.slug??slugify(page.name)));while(used.has(slug))slug=`${root}-${index++}`;return slug;},[]);
  const addPage=useCallback(()=>{const id=`page_${Date.now()}`;setDocAndMarkDirty(prev=>{const name=`Page ${prev.project.pages.length+1}`;return{...prev,project:{...prev.project,pages:[...prev.project.pages,{id,name,slug:uniqueSlug(name,prev.project.pages),order:prev.project.pages.length,showInNavigation:true,components:[]}]}}});setActivePageId(id);setSelectedId(null);},[setDocAndMarkDirty,uniqueSlug]);
  const duplicatePage=useCallback((id:string)=>{let nextId='';setDocAndMarkDirty(prev=>{const source=prev.project.pages.find(page=>page.id===id);if(!source)return prev;nextId=`page_${Date.now()}`;const name=`${source.name} Copy`;const clone:Page={...source,id:nextId,name,slug:uniqueSlug(name,prev.project.pages),order:prev.project.pages.length,components:source.components.map(component=>({...component,id:`${component.id}_${Date.now()}`}))};return{...prev,project:{...prev.project,pages:[...prev.project.pages,clone]}}});if(nextId)setActivePageId(nextId);setSelectedId(null);},[setDocAndMarkDirty,uniqueSlug]);
  const updatePage=useCallback((id:string,changes:Partial<Pick<Page,'name'|'slug'|'showInNavigation'>>)=>setDocAndMarkDirty(prev=>({...prev,project:{...prev.project,pages:prev.project.pages.map(page=>page.id===id?{...page,...changes}:page)}})),[setDocAndMarkDirty]);
  const movePage=useCallback((id:string,direction:-1|1)=>setDocAndMarkDirty(prev=>{const pages=[...prev.project.pages].sort((a,b)=>(a.order??0)-(b.order??0));const index=pages.findIndex(page=>page.id===id),target=index+direction;if(index<0||target<0||target>=pages.length)return prev;[pages[index],pages[target]]=[pages[target],pages[index]];return{...prev,project:{...prev.project,pages:pages.map((page,order)=>({...page,order}))}};}),[setDocAndMarkDirty]);
  const deletePage=useCallback((id:string)=>setDocAndMarkDirty(prev=>{if(prev.project.pages.length<=1)return prev;const pages=prev.project.pages.filter(page=>page.id!==id).map((page,order)=>({...page,order}));const variables=Object.fromEntries(Object.entries(prev.project.variables).filter(([,variable])=>variable.pageId!==id));const defaultPageId=prev.project.settings.defaultPageId===id?pages[0].id:prev.project.settings.defaultPageId;setActivePageId(pages[0].id);setSelectedId(null);return{...prev,project:{...prev.project,pages,variables,settings:{...prev.project.settings,defaultPageId}}};}),[setDocAndMarkDirty]);
  const setDefaultPage=useCallback((id:string)=>setDocAndMarkDirty(prev=>({...prev,project:{...prev.project,settings:{...prev.project.settings,defaultPageId:id}}})),[setDocAndMarkDirty]);

  // ── Component selection ───────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectComponent = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  // ── Persistence state ─────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [validationPending, setValidationPending] = useState(false);
  const [validationUnavailable, setValidationUnavailable] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setValidationPending(true);
    const timer = setTimeout(() => {
      void validateProject(doc).then((result) => {
        if (!active) return;
        setValidation(result);
        setValidationUnavailable(null);
      }).catch((error) => {
        if (!active) return;
        setValidation(null);
        setValidationUnavailable(error instanceof Error ? error.message : String(error));
      }).finally(() => { if (active) setValidationPending(false); });
    }, 150);
    return () => { active = false; clearTimeout(timer); };
  }, [doc]);

  // ── Project list ──────────────────────────────────────────────────────────
  const [projectList, setProjectList] = useState<ApiProjectRow[]>([]);

  // ── Toast notifications ───────────────────────────────────────────────────
  const [toasts, setToasts] = useState<Toast[]>([]);
  let _toastId = 0;

  const addToast = useCallback((kind: ToastKind, message: string) => {
    const id = ++_toastId;
    setToasts((prev) => [...prev, { id, kind, message }]);
    // Auto-dismiss after 4 s
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── setProjectName ────────────────────────────────────────────────────────
  const setProjectName = useCallback((name: string) => {
    setProjectNameState(name);
    setDoc((prev) => ({
      ...prev,
      project: { ...prev.project, name },
    }));
    setIsDirty(true);
  }, []);

  // ── Operations ────────────────────────────────────────────────────────────

  const newProject = useCallback((name = 'New Project') => {
    const freshDoc = makeNewDoc(name);
    setDoc(freshDoc);
    setProjectRowId(null);
    setProjectNameState(name);
    setSelectedId(null);
    setActivePageId(freshDoc.project.pages[0].id);
    setIsDirty(false);
  }, []);

  const saveProject = useCallback(async () => {
    setIsSaving(true);
    try {
      // Ensure the in-memory doc reflects the current project name
      const docToSave: ProjectDocument = {
        ...doc,
        project: { ...doc.project, name: projectName },
      };

      const checked = await validateProject(docToSave);
      setValidation(checked);
      setValidationUnavailable(null);
      if (!checked.valid) {
        const summary = checked.errors.slice(0, 3).map((issue) => `${issue.path}: ${issue.message}`).join('; ');
        throw new Error(`Validation failed. ${summary}`);
      }

      let row: ApiProjectRow;
      if (projectRowId === null) {
        // First save — create a new record
        row = await api.createProject(projectName, doc.project.description ?? '', docToSave);
        setProjectRowId(row.id);
      } else {
        // Subsequent save — update existing record
        row = await api.saveProject(projectRowId, projectName, doc.project.description ?? '', docToSave);
      }

      // Sync doc.project.id to the backend row id (string form in the JSON)
      setDoc((prev) => ({
        ...prev,
        project: { ...prev.project, id: String(row.id) },
      }));
      setIsDirty(false);
      addToast('success', `Project "${projectName}" saved.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      addToast('error', `Save failed: ${message}`);
    } finally {
      setIsSaving(false);
    }
  }, [doc, projectRowId, projectName, addToast]);

  const loadProject = useCallback(async (id: number) => {
    setIsLoading(true);
    setSelectedId(null);
    try {
      const row = await api.getProject(id);
      let loadedDoc: ProjectDocument;
      try {
        loadedDoc = JSON.parse(row.project_json) as ProjectDocument;
      } catch {
        throw new Error('Project data is not valid JSON.');
      }
      setDoc(loadedDoc);
      setActivePageId(loadedDoc.project.settings.defaultPageId??loadedDoc.project.pages[0].id);
      setProjectRowId(row.id);
      setProjectNameState(row.name);
      setIsDirty(false);
      addToast('success', `Loaded "${row.name}".`);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      addToast('error', `Load failed: ${message}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  const refreshProjectList = useCallback(async () => {
    try {
      const list = await api.listProjects();
      setProjectList(list);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      addToast('error', `Could not fetch project list: ${message}`);
    }
  }, [addToast]);

  // ── Context value (memoised to avoid unnecessary re-renders) ─────────────
  const value = useMemo<ProjectContextValue>(
    () => ({
      doc,
      setDoc: setDocAndMarkDirty,
      projectRowId,
      projectName,
      setProjectName,
      activePage: canvas.activePage,
      activePageId,
      setActivePage,addPage,duplicatePage,updatePage,movePage,deletePage,setDefaultPage,
      canvas,
      selectedId,
      selectComponent,
      isDirty,
      isLoading,
      isSaving,
      validation,
      validationPending,
      validationUnavailable,
      projectList,
      newProject,
      saveProject,
      loadProject,
      refreshProjectList,
      toasts,
      dismissToast,
    }),
    [
      doc,
      setDocAndMarkDirty,
      projectRowId,
      projectName,
      setProjectName,
      canvas,
      activePageId,setActivePage,addPage,duplicatePage,updatePage,movePage,deletePage,setDefaultPage,
      selectedId,
      selectComponent,
      isDirty,
      isLoading,
      isSaving,
      validation,
      validationPending,
      validationUnavailable,
      projectList,
      newProject,
      saveProject,
      loadProject,
      refreshProjectList,
      toasts,
      dismissToast,
    ],
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

// ── Consumer hook ─────────────────────────────────────────────────────────────

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error('useProject must be used inside <ProjectProvider>');
  }
  return ctx;
}
